import { Component, Inject, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ManagementService } from '../../management.service';
import { UtilService } from '../../../../services/util.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { defaultValidFrom } from '../research-section/research-dates';

export interface ResearchPromptData {
  brandKey: string;
  brandName: string;
}

/**
 * Post-creation prompt (DEV-522): offered right after a brand with social sources is created, so
 * sales can queue research immediately instead of discovering days later that the brand has no
 * metrics. One-click with the default backdate; custom dates live in Edit brand -> Research -> Run….
 */
@Component({
  selector: 'app-research-prompt',
  imports: [CommonModule, ButtonComponent],
  templateUrl: './research-prompt.component.html',
  styleUrl: './research-prompt.component.scss',
})
export class ResearchPromptComponent {
  public readonly triggering = signal(false);
  public readonly error = signal<string | null>(null);
  public readonly estimatesFrom = defaultValidFrom();

  private readonly api = inject(ManagementService);
  private readonly utilService = inject(UtilService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ResearchPromptData,
    private readonly dialogRef: MatDialogRef<ResearchPromptComponent>
  ) {}

  public run(): void {
    if (this.triggering()) return;
    this.triggering.set(true);
    this.error.set(null);
    this.dialogRef.disableClose = true; // don't lose the outcome to a backdrop click mid-request
    // Deliberately NOT takeUntilDestroyed: HttpClient aborts the request on unsubscribe, so tying
    // it to the dialog's lifetime would let a dismissal silently cancel a queue the user believes
    // they submitted. The request completes regardless; the snackbar (root service) still reports.
    this.api.triggerBrandResearch(this.data.brandKey)
      .subscribe({
        next: () => {
          this.dialogRef.disableClose = false;
          this.utilService.openSnackBar('Research queued — it will be picked up within ~10 minutes', { variant: 'success', position: 'center', duration: 4000 });
          this.dialogRef.close(true);
        },
        error: error => {
          this.dialogRef.disableClose = false;
          this.triggering.set(false);
          const serverMessage = error?.status >= 400 && error?.status < 500 ? error?.error?.message : null;
          this.error.set(serverMessage ?? 'Failed to queue research — you can run it later from Edit brand.');
        },
      });
  }

  public notNow(): void {
    this.dialogRef.close(false);
  }
}
